import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { userService } from '../../services/userService';
import { SurveyorProfile as SurveyorProfileType, Rating, SURVEY_CATEGORIES, SURVEY_SERVICE_TYPES } from '../../types';
import { apiClient } from '../../services/apiClient';
import { getSurveyorRank } from '../../utils/surveyorRank';
import { toNumber } from '../../utils/number';
import { getNearestZimbabweCity } from '../../utils/jobLocation';
import { MapPicker } from '../../components/MapPicker';

export const SurveyorProfile: React.FC = () => {
  const { user } = useAuthStore();
  const [profile, setProfile] = useState<SurveyorProfileType | null>(null);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ latitude: null as number | null, longitude: null as number | null, is_available: true, service_types: [] as string[] });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      const p = await userService.getSurveyorProfile(user.id);
      setProfile(p);
      setEditForm({ latitude: p.latitude ?? null, longitude: p.longitude ?? null, is_available: p.is_available, service_types: [...(p.service_types ?? [])] });
      try {
        const { data } = await apiClient.get(`/surveyors/${user.id}/ratings/`);
        const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        setRatings(list);
      } catch { /* ratings may 404 if none */ }
    } catch (err: any) {
      setError(err?.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveMsg('');
    try {
      const updated = await userService.updateSurveyorProfile({
        latitude: editForm.latitude ?? undefined,
        longitude: editForm.longitude ?? undefined,
        is_available: editForm.is_available,
        service_types: editForm.service_types,
      });
      setProfile(updated);
      setIsEditing(false);
      setSaveMsg('Profile updated successfully!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err: any) {
      setSaveMsg(err?.message || 'Failed to save');
    } finally {
      setSaveLoading(false);
    }
  };

  const toggleServiceType = (st: string) => {
    setEditForm(prev => ({
      ...prev,
      service_types: (prev.service_types ?? []).includes(st) ? prev.service_types.filter(x => x !== st) : [...(prev.service_types ?? []), st],
    }));
  };

  if (isLoading) return <div style={s.center}><div style={s.spinner} /><p style={{ color: '#636E72' }}>Loading profile…</p></div>;
  if (error) return <div style={s.center}><p style={{ color: '#E74C3C' }}>{error}</p></div>;
  if (!profile) return null;

  const verificationColors: Record<string, { bg: string; color: string }> = {
    APPROVED: { bg: 'var(--sc-success-bg)', color: 'var(--sc-success-text)' },
    PENDING: { bg: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)' },
    REJECTED: { bg: 'var(--sc-error-bg)', color: 'var(--sc-error-text)' },
  };
  const PRESENCE: Record<string, { color: string; label: string; bg: string }> = {
    online:  { color: '#065F46', label: 'Online',  bg: '#D1FAE5' },
    away:    { color: '#92400E', label: 'Away',    bg: '#FEF3C7' },
    offline: { color: '#991B1B', label: 'Offline', bg: '#FEE2E2' },
  };
  const presenceDotColors: Record<string, string> = {
    online: '#10B981', away: '#F59E0B', offline: '#EF4444',
  };
  const vc = verificationColors[profile.verification_status] || verificationColors.PENDING;
  const ratingAverage = toNumber(profile.rating_average);
  const totalJobs = toNumber(profile.total_jobs);
  const rank = getSurveyorRank(ratingAverage, totalJobs);
  const isVerified = profile.verification_status === 'APPROVED';
  const presence = PRESENCE[profile.online_status ?? 'offline'] ?? PRESENCE.offline;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={s.title}>My Profile</h1>
        {!isEditing ? (
          <button onClick={() => setIsEditing(true)} style={{ padding: '10px 20px', background: '#10B981', color: '#FFF', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            ✏️ Edit Profile
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} disabled={saveLoading} style={{ padding: '10px 20px', background: '#10B981', color: '#FFF', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: saveLoading ? 0.6 : 1 }}>
              {saveLoading ? 'Saving...' : '✓ Save'}
            </button>
            <button onClick={() => { setIsEditing(false); if (profile) setEditForm({ latitude: profile.latitude ?? null, longitude: profile.longitude ?? null, is_available: profile.is_available, service_types: [...profile.service_types] }); }}
              style={{ padding: '10px 20px', background: '#F5F6FA', color: '#636E72', border: '1px solid #E8ECF0', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {saveMsg && <div style={{ padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600, background: saveMsg.includes('success') ? '#D1FAE5' : '#FEE2E2', color: saveMsg.includes('success') ? '#065F46' : '#DC2626' }}>{saveMsg}</div>}

      <div style={s.gridTwo}>
        {/* Profile Overview */}
        <div style={s.card}>
          <div style={s.profileHeader}>
            <div style={{ position: 'relative' as const }}>
              <div style={s.avatarLg}>
                {profile.first_name?.[0] || '?'}{profile.last_name?.[0] || '?'}
              </div>
              {isVerified && (
                <div style={{ position: 'absolute' as const, bottom: -4, right: -4, width: 22, height: 22, borderRadius: '50%', background: '#10B981', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, border: '2px solid #FFF', boxShadow: '0 2px 6px rgba(16,185,129,0.4)' }} title="Verified">✓</div>
              )}
            </div>
            <div>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: '#2D3436', margin: '0 0 6px' }}>
                {profile.first_name} {profile.last_name}
              </h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
                <span style={{ ...s.badge, background: vc.bg, color: vc.color }}>
                  {isVerified ? '✓ Verified' : profile.verification_status}
                </span>
                <span style={{ ...s.badge, background: rank.bg, color: rank.color }}>
                  {rank.icon} {rank.label}
                </span>
                <span style={{ ...s.badge, background: presence.bg, color: presence.color, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: presenceDotColors[profile.online_status ?? 'offline'] ?? '#EF4444', display: 'inline-block', flexShrink: 0 }} />
                  {presence.label}
                </span>
              </div>
            </div>
          </div>

          <div style={s.divider} />

          <div style={s.fieldGrid}>
            <InfoField label="Phone" value={profile.phone} />
            <InfoField label="Email" value={profile.email || '—'} />
            {isEditing ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#636E72', textTransform: 'uppercase' as const, marginBottom: 3 }}>Base Location</div>
                  <MapPicker
                    latitude={editForm.latitude}
                    longitude={editForm.longitude}
                    onChange={(lat: number, lng: number) => setEditForm(f => ({ ...f, latitude: lat, longitude: lng }))}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#636E72', textTransform: 'uppercase' as const, marginBottom: 3 }}>Available</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={editForm.is_available} onChange={(e) => setEditForm(f => ({ ...f, is_available: e.target.checked }))} />
                    <span style={{ fontSize: 14 }}>{editForm.is_available ? 'Yes' : 'No'}</span>
                  </label>
                </div>
              </div>
            ) : (
              <>
                <InfoField label="Base City" value={
                  (profile.latitude && profile.longitude) 
                    ? (getNearestZimbabweCity({ latitude: Number(profile.latitude), longitude: Number(profile.longitude) })?.city || 'Unknown') 
                    : 'Not Set'
                } />
                <InfoField label="Coordinates" value={profile.latitude && profile.longitude ? `${Number(profile.latitude).toFixed(5)}, ${Number(profile.longitude).toFixed(5)}` : '—'} />
                <InfoField label="Available" value={profile.is_available ? 'Yes' : 'No'} />
              </>
            )}
            <InfoField label="Total Jobs" value={String(profile.total_jobs)} />
          </div>

          <div style={s.divider} />

          <h3 style={s.sectionTitle}>Service Types</h3>
          {isEditing ? (
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6 }}>
              {SURVEY_SERVICE_TYPES.map((st) => (
                <button key={st} type="button" onClick={() => toggleServiceType(st)}
                  style={{
                    padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                    ...(editForm.service_types.includes(st)
                      ? { background: '#ECFDF5', color: '#10B981', borderColor: '#10B981' }
                      : { background: '#F5F6FA', color: '#636E72', borderColor: '#E8ECF0' }),
                  }}>{st}</button>
              ))}
            </div>
          ) : (
            <div style={s.chipRow}>
              {(profile.service_types ?? []).map((st) => (
                <span key={st} style={s.chip}>{st}</span>
              ))}
            </div>
          )}

          <div style={s.divider} />

          <h3 style={s.sectionTitle}>Verification Documents</h3>
          {(profile.verification_docs ?? []).length === 0 ? (
            <p style={{ color: '#636E72', fontSize: 13 }}>No documents uploaded.</p>
          ) : (
            <ul style={{ paddingLeft: 20, margin: '8px 0 0' }}>
              {(profile.verification_docs ?? []).map((doc, i) => (
                <li key={i} style={{ fontSize: 13, color: '#2D3436', marginBottom: 4 }}>
                  <a href={doc} target="_blank" rel="noopener noreferrer" style={{ color: '#0C5ADB' }}>Document {i + 1}</a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Performance & Ratings */}
        <div>
          {/* Performance */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>Performance</h3>
            <div style={s.statRow}>
              <div style={s.statBox}>
                <div style={s.statVal}>{ratingAverage.toFixed(1)}</div>
                <div style={s.statLabel}>Rating</div>
                <div style={{ fontSize: 18 }}>
                  {'★'.repeat(Math.round(ratingAverage))}
                  {'☆'.repeat(5 - Math.round(ratingAverage))}
                </div>
              </div>
              <div style={s.statBox}>
                <div style={s.statVal}>{profile.total_jobs}</div>
                <div style={s.statLabel}>Total Jobs</div>
              </div>
            </div>
          </div>

          {/* Ratings list */}
          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={s.cardTitle}>Ratings & Reviews ({ratings.length})</h3>
            {ratings.length === 0 ? (
              <p style={{ color: '#636E72', fontSize: 13 }}>No reviews yet.</p>
            ) : (
              ratings.map((r) => (
                <div key={r.id} style={s.ratingItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#2D3436' }}>{r.from_user?.first_name} {r.from_user?.last_name}</span>
                    <span style={{ fontSize: 14, color: '#F59E0B' }}>{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</span>
                  </div>
                  {r.comment && <p style={{ fontSize: 13, color: '#636E72', margin: 0 }}>{r.comment}</p>}
                  <div style={{ fontSize: 11, color: '#B2BEC3', marginTop: 4 }}>{new Date(r.created_at).toLocaleDateString('en-ZW')}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--sc-text-secondary)', textTransform: 'uppercase' as const, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: 'var(--sc-text-primary)' }}>{value}</div>
  </div>
);

const s: Record<string, React.CSSProperties> = {
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  title: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 24px' },

  gridTwo: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 16, padding: 20, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)', marginBottom: 16 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px' },
  profileHeader: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' as const },
  avatarLg: { width: 64, height: 64, borderRadius: 16, background: 'var(--sc-brand-gradient)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22 },
  badge: { padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700 },
  divider: { height: 1, background: 'var(--sc-border-light)', margin: '16px 0' },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 12px' },
  fieldGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px 16px' },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: { padding: '6px 12px', background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', borderRadius: 8, fontSize: 12, fontWeight: 600 },

  statRow: { display: 'flex', gap: 16 },
  statBox: { flex: 1, textAlign: 'center' as const, padding: 16, background: 'var(--sc-bg-primary)', borderRadius: 12 },
  statVal: { fontSize: 28, fontWeight: 800, color: 'var(--sc-text-primary)' },
  statLabel: { fontSize: 12, color: 'var(--sc-text-secondary)', fontWeight: 500, marginTop: 4 },

  ratingItem: { padding: '14px 0', borderBottom: '1px solid var(--sc-border)' },
};
